// Utility functions for data storage and management

export interface Submission {
  id: string;
  type: 'project' | 'resource';
  submittedAt: string;
  status: 'new' | 'reviewed' | 'completed';
  data: any;
}

export interface ProjectRequestData {
  companyName: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  projectName: string;
  projectDescription?: string;
  projectType: string;
  technologyPreferences?: string;
  estimatedBudgetRange?: string;
  timeline: string;
  preferredStartDate: string;
  additionalRequirements?: string;
}

export interface ResourceRequestData {
  companyName: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  projectTeamName: string;
  numberOfResourcesNeeded: number;
  resourceDetails: ResourceDetail[];
  contractDuration: string;
  workArrangement: 'remote' | 'on-site' | 'hybrid';
  location?: string;
  preferredStartDate: string;
  budgetPerResource?: string;
  additionalNotes?: string;
}

export interface ResourceDetail {
  role: string;
  seniority: string;
  requiredSkills: string;
  quantity: number;
}

// Generate unique ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

// Save submission to localStorage (simulating JSON file storage)
export const saveSubmission = (submission: Omit<Submission, 'id' | 'submittedAt'>): string => {
  const id = generateId();
  const timestamp = new Date().toISOString();
  
  const fullSubmission: Submission = {
    ...submission,
    id,
    submittedAt: timestamp,
  };

  // Get existing submissions
  const existingSubmissions = getSubmissions();
  const updatedSubmissions = [...existingSubmissions, fullSubmission];
  
  // Save to localStorage
  localStorage.setItem('submissions', JSON.stringify(updatedSubmissions));
  
  return id;
};

// Get all submissions
export const getSubmissions = (): Submission[] => {
  const submissions = localStorage.getItem('submissions');
  return submissions ? JSON.parse(submissions) : [];
};

// Get submission by ID
export const getSubmissionById = (id: string): Submission | null => {
  const submissions = getSubmissions();
  return submissions.find(sub => sub.id === id) || null;
};

// Update submission status
export const updateSubmissionStatus = (id: string, status: Submission['status']): boolean => {
  const submissions = getSubmissions();
  const submissionIndex = submissions.findIndex(sub => sub.id === id);
  
  if (submissionIndex === -1) return false;
  
  submissions[submissionIndex].status = status;
  localStorage.setItem('submissions', JSON.stringify(submissions));
  return true;
};

// Delete submission
export const deleteSubmission = (id: string): boolean => {
  const submissions = getSubmissions();
  const filteredSubmissions = submissions.filter(sub => sub.id !== id);
  
  if (filteredSubmissions.length === submissions.length) return false;
  
  localStorage.setItem('submissions', JSON.stringify(filteredSubmissions));
  return true;
};

// Filter submissions
export const filterSubmissions = (
  submissions: Submission[],
  filters: {
    type?: 'all' | 'project' | 'resource';
    status?: 'all' | 'new' | 'reviewed' | 'completed';
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }
): Submission[] => {
  return submissions.filter(submission => {
    // Type filter
    if (filters.type && filters.type !== 'all' && submission.type !== filters.type) {
      return false;
    }
    
    // Status filter
    if (filters.status && filters.status !== 'all' && submission.status !== filters.status) {
      return false;
    }
    
    // Date range filter
    if (filters.dateFrom && submission.submittedAt < filters.dateFrom) {
      return false;
    }
    if (filters.dateTo && submission.submittedAt > filters.dateTo) {
      return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const searchableText = [
        submission.data.companyName,
        submission.data.contactPerson,
        submission.data.email,
        submission.type === 'project' ? submission.data.projectName : submission.data.projectTeamName
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });
};

// Auto-save form data to localStorage
export const autoSaveFormData = (type: 'project' | 'resource', data: any): void => {
  const key = `form_${type}_autosave`;
  localStorage.setItem(key, JSON.stringify(data));
};

// Load auto-saved form data
export const loadAutoSavedFormData = (type: 'project' | 'resource'): any => {
  const key = `form_${type}_autosave`;
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

// Clear auto-saved form data
export const clearAutoSavedFormData = (type: 'project' | 'resource'): void => {
  const key = `form_${type}_autosave`;
  localStorage.removeItem(key);
};

// Export submissions as JSON
export const exportSubmissions = (submissions: Submission[]): string => {
  return JSON.stringify(submissions, null, 2);
};
